// DTOs/schemas do módulo "leads" (captação e repasse). Pertence ao pacote leads.
import { z } from "zod";

import { STATUS_LEAD, type StatusLead } from "./enums";

// ─── Vocabulário ─────────────────────────────────────────────────────────────

export const ORIGENS_LEAD = ["CAMPANHA", "ANUNCIO", "ORGANICO"] as const;
export type OrigemLead = (typeof ORIGENS_LEAD)[number];
export const zOrigemLead = z.enum(ORIGENS_LEAD);
export const zStatusLead = z.enum(STATUS_LEAD);

/** Gatilhos de criação de lead no número oficial (mesmos valores da origem). */
export const GATILHOS_LEAD = ORIGENS_LEAD;
export type GatilhoLead = OrigemLead;
export const zGatilhoLead = zOrigemLead;

/** Horas do repasse até o lead virar `SEM_RESPOSTA`. */
export const HORAS_LEAD_SEM_RESPOSTA = 72;

/** Variáveis aceitas na mensagem de primeiro contato (`{{nome}}`...). */
export const VARIAVEIS_PRIMEIRO_CONTATO = ["nome", "origem", "primeiraMensagem"] as const;
/** Variáveis aceitas no aviso pelo número oficial. */
export const VARIAVEIS_AVISO_LEAD = ["numero", "nome", "origem"] as const;

const RE_VARIAVEL = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;

/** Nomes (sem chaves) das variáveis `{{x}}` presentes no texto. */
export function variaveisDoTemplate(texto: string): string[] {
  const nomes: string[] = [];
  for (const m of texto.matchAll(RE_VARIAVEL)) nomes.push(m[1] ?? "");
  return nomes;
}

// ─── Entradas ────────────────────────────────────────────────────────────────

const CUID = /^c[a-z0-9]{20,30}$/;
const idPublico = z.string().trim().regex(CUID, "Identificador inválido.");

/** `YYYY-MM-DD` vale o dia inteiro em America/Sao_Paulo (UTC-3, sem horário de verão); ISO completo vale como está. */
function data(fim: boolean) {
  return z
    .string()
    .trim()
    .min(1)
    .max(40)
    .transform((s, ctx) => {
      const somenteData = /^\d{4}-\d{2}-\d{2}$/.test(s);
      const d = new Date(somenteData ? `${s}T${fim ? "23:59:59.999" : "00:00:00.000"}-03:00` : s);
      if (Number.isNaN(d.getTime())) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Data inválida." });
        return z.NEVER;
      }
      return d;
    });
}

export const zConsultaLeads = z.object({
  status: zStatusLead.optional(),
  origem: zOrigemLead.optional(),
  /** publicId do número OFICIAL de origem. */
  numeroOficialId: idPublico.optional(),
  /** publicId do número NAO_OFICIAL a que o lead foi repassado. */
  numeroAtribuidoId: idPublico.optional(),
  /** publicId da campanha. */
  campanhaId: idPublico.optional(),
  anuncioId: z.string().trim().min(1).max(100).optional(),
  de: data(false).optional(),
  ate: data(true).optional(),
  limite: z.coerce.number().int().min(1).max(100).default(30),
  /** Opaco: devolvido em `proximoCursor`. */
  cursor: z.string().trim().min(1).max(100).optional(),
});
export type ConsultaLeads = z.infer<typeof zConsultaLeads>;

export const zConsultaFunilLeads = z.object({
  numeroOficialId: idPublico.optional(),
  campanhaId: idPublico.optional(),
  de: data(false).optional(),
  ate: data(true).optional(),
});
export type ConsultaFunilLeads = z.infer<typeof zConsultaFunilLeads>;

/** `numeroId` ausente = repasse automático (próximo do rodízio da regra do oficial). */
export const zRepassarLead = z.object({ numeroId: idPublico.optional() });
export type RepassarLeadInput = z.infer<typeof zRepassarLead>;

function validarVariaveis(
  texto: string,
  permitidas: readonly string[],
  caminho: string,
  ctx: z.RefinementCtx,
): void {
  const invalidas = [...new Set(variaveisDoTemplate(texto).filter((v) => !permitidas.includes(v)))];
  if (invalidas.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [caminho],
      message: `Variável desconhecida: ${invalidas.map((v) => `{{${v}}}`).join(", ")}. Use ${permitidas
        .map((v) => `{{${v}}}`)
        .join(", ")}.`,
    });
  }
}

/** `PUT /numeros/:id/repasse` — substitui a regra inteira do número oficial. */
export const zSalvarRegraRepasse = z
  .object({
    ativo: z.boolean().default(false),
    automatico: z.boolean().default(false),
    gatilhos: z
      .array(zGatilhoLead)
      .max(ORIGENS_LEAD.length)
      .default([...GATILHOS_LEAD])
      .transform((l) => [...new Set(l)]),
    /** publicIds dos números NAO_OFICIAL do grupo, na ordem do rodízio. */
    destinoIds: z
      .array(idPublico)
      .max(20, "No máximo 20 números no grupo.")
      .default([])
      .refine((l) => new Set(l).size === l.length, "Há números repetidos no grupo."),
    mensagemPrimeiroContato: z.string().trim().max(1000, "Máximo de 1000 caracteres.").default(""),
    avisoPeloOficial: z.boolean().default(false),
    textoAviso: z.string().trim().max(500, "Máximo de 500 caracteres.").default(""),
  })
  .superRefine((d, ctx) => {
    validarVariaveis(d.mensagemPrimeiroContato, VARIAVEIS_PRIMEIRO_CONTATO, "mensagemPrimeiroContato", ctx);
    validarVariaveis(d.textoAviso, VARIAVEIS_AVISO_LEAD, "textoAviso", ctx);
    if (d.ativo && !d.mensagemPrimeiroContato) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["mensagemPrimeiroContato"],
        message: "Informe a mensagem de primeiro contato para ligar o repasse.",
      });
    }
    if (d.ativo && d.automatico && d.destinoIds.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["destinoIds"],
        message: "Escolha ao menos um número não oficial para o repasse automático.",
      });
    }
    if (d.avisoPeloOficial && !d.textoAviso) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["textoAviso"],
        message: "Informe o texto do aviso ou desligue o aviso pelo oficial.",
      });
    }
  });
export type SalvarRegraRepasseInput = z.infer<typeof zSalvarRegraRepasse>;

// ─── Saídas ──────────────────────────────────────────────────────────────────

export interface LeadNumeroRef {
  /** publicId */
  id: string;
  apelido: string;
  telefone: string | null;
}

export interface LeadEconomia {
  /** Mensagens do lead que geraram economia (fora da janela, em número não oficial). */
  qtd: number;
  marketingCentavos: number;
  utilityCentavos: number;
}

export interface LeadResumo {
  /** publicId */
  id: string;
  contatoTelefone: string;
  contatoNome: string | null;
  origem: OrigemLead;
  campanha: { id: string; nome: string } | null;
  anuncio: { id: string; titulo: string | null; url: string | null } | null;
  primeiraMensagem: string | null;
  status: StatusLead;
  numeroOficial: LeadNumeroRef;
  numeroAtribuido: LeadNumeroRef | null;
  /** publicId da conversa no número oficial. */
  conversaId: string | null;
  /** O contato pediu para sair (SAIR/PARAR/STOP): o repasse automático não acontece. */
  contatoOptout: boolean;
  repassadoEm: string | null;
  respondeuEm: string | null;
  criadoEm: string;
  economia: LeadEconomia;
}

export interface ListaLeads {
  itens: LeadResumo[];
  proximoCursor: string | null;
}

export interface ResultadoRepasseLead {
  lead: LeadResumo;
  /** O contato está em opt-out: o repasse manual/API foi feito mesmo assim, sinalizado. */
  contatoOptout: boolean;
}

/** Etapas do funil. Taxas em percentual (0–100, uma casa decimal). */
export interface EtapasFunil {
  captados: number;
  repassados: number;
  responderam: number;
  semResposta: number;
  /** repassados / captados */
  taxaRepasse: number;
  /** responderam / repassados */
  taxaResposta: number;
  /** semResposta / repassados */
  taxaSemResposta: number;
}

export interface FunilLeads {
  total: EtapasFunil;
  porOrigem: Array<{ origem: OrigemLead } & EtapasFunil>;
  porCampanha: Array<{ campanhaId: string; nome: string } & EtapasFunil>;
  porAnuncio: Array<{ anuncioId: string; titulo: string | null } & EtapasFunil>;
}

export interface RegraRepasseDto {
  /** publicId do número oficial. */
  numeroOficialId: string;
  ativo: boolean;
  automatico: boolean;
  gatilhos: GatilhoLead[];
  destinos: Array<LeadNumeroRef & { status: string }>;
  mensagemPrimeiroContato: string;
  avisoPeloOficial: boolean;
  textoAviso: string;
}

/** Nome dos eventos em tempo real e de saída de leads (referência para o front). */
export const EVENTO_TEMPO_REAL_LEAD = "lead.atualizado" as const;
